"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Phone, MessageSquare, RefreshCw, Check, Sparkles, ArrowRight, Shield, Activity, Terminal, Zap, Bot, Clock, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const stats = [{
  label: "AI Search Rank",
  value: "#1 Spot",
  detail: "in local queries"
}, {
  label: "Response Speed",
  value: "< 15s",
  detail: "24/7 instant dispatch"
}, {
  label: "Accuracy Rate",
  value: "99.4%",
  detail: "verified citations"
}];
const voiceTranscript = [{
  sender: "User",
  role: "caller",
  time: "10:42 AM",
  text: "Hi, I'm looking for a plumber in the garland area ASAP!"
}, {
  sender: "ClearLine Voice AI",
  role: "ai",
  time: "10:42 AM",
  text: "I can help with that. Plumbing .Co can have someone out there right away! What's your address?"
}, {
  sender: "User",
  role: "caller",
  time: "10:43 AM",
  text: "Yes please, my address is 420 Oak Street."
}, {
  sender: "ClearLine Voice AI",
  role: "ai",
  time: "10:43 AM",
  text: "Confirmed! Technician Marcus is dispatched to 420 Oak Street. Confirmation sent to your phone."
}];
const chatTranscript = [{
  sender: "Visitor",
  role: "caller",
  time: "11:15 AM",
  text: "Do you offer free AI visibility checkups for local contractors?"
}, {
  sender: "ClearLine Web AI",
  role: "ai",
  time: "11:15 AM",
  text: "Yes! We analyze how ChatGPT, Perplexity, and Google Gemini rank your business against local competitors."
}, {
  sender: "Visitor",
  role: "caller",
  time: "11:16 AM",
  text: "How long does the audit take to complete?"
}, {
  sender: "ClearLine Web AI",
  role: "ai",
  time: "11:16 AM",
  text: "Our audit delivers your complete visibility report and actionable clean-up plan within 48 hours."
}];
const followupTranscript = [{
  sender: "System",
  role: "ai",
  time: "01:05 PM",
  text: "Automated SMS review request dispatched to John M. following HVAC repair."
}, {
  sender: "Customer SMS",
  role: "caller",
  time: "01:12 PM",
  text: "5 stars! Tech arrived on time and fixed the AC unit immediately."
}, {
  sender: "ClearLine Reputation AI",
  role: "ai",
  time: "01:12 PM",
  text: "Feedback recorded. Verified 5-star review published to Google Business Profile & local AI citations."
}];
const quickActions = ["Check AI Visibility", "Book Dispatch Call", "Run GEO Audit"];
const liveFeedNotifications = [{
  id: 1,
  title: "Perplexity Cited Business #1",
  subtitle: "Matched query: 'Best local plumber in Dallas'",
  tag: "Citation Sync",
  time: "Just now"
}, {
  id: 2,
  title: "ChatGPT Recommended ClearLine",
  subtitle: "Lead converted via automated booking link",
  tag: "Lead Won",
  time: "2m ago"
}];
export default function Console() {
  const [activeTab, setActiveTab] = useState<string>("voice");
  const [quickInput, setQuickInput] = useState<string>("");
  const shouldReduceMotion = useReducedMotion();
  const handleActionClick = (actionText: string) => {
    setQuickInput(actionText);
  };
  return <section id="interactiveaiconsolehero" data-nav="dark" className="relative bg-background text-foreground py-12 md:py-20 lg:py-24 overflow-hidden border-b border-border">
      <div className="absolute inset-0 bg-radial from-primary/10 via-transparent to-transparent opacity-60 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-5 flex flex-col gap-6">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="flex items-center gap-2">
              <Badge variant="outline" className="bg-muted text-foreground border-border px-3 py-1 text-xs font-mono tracking-wider uppercase flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-[var(--accent-deep-red)]" />
                AI Visibility & Conversational Platform
              </Badge>
            </motion.div>

            <motion.h1 initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground leading-tight">Never miss a potential client again.</motion.h1>

            <motion.p initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-base sm:text-lg text-muted-foreground leading-relaxed">Clearline deploys 24/7 AI receptionist tools so local service businesses never have to hound phones and scramble for texts again. </motion.p>

            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button asChild size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-10 rounded-md has-[>svg]:px-4 text-destructive-foreground font-semibold px-6 shadow-md !bg-[var(--accent-deep-red)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-[var(--accent-deep-red)]/90">
                <Link to="/PricingPlansPage"></Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-medium px-6">
                <Link to="/ContactPage">
                  Free AI visibility checkup
                </Link>
              </Button>
            </motion.div>
          </div>

          <div className="lg:col-span-7">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }}>
              <Card className="bg-card border-border shadow-2xl overflow-hidden rounded-xl">
                <div className="bg-muted/80 px-4 py-3 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <span className="size-3 rounded-full bg-[var(--accent-deep-red)]/80 inline-block" />
                      <span className="size-3 rounded-full bg-chart-5/80 inline-block" />
                      <span className="size-3 rounded-full bg-chart-4/80 inline-block" />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground ml-2 flex items-center gap-1.5">
                      <Terminal className="size-3.5" />
                      clearline-ai-console ~ v2.4
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-card/80 text-foreground border-border text-[11px] font-mono flex items-center gap-1.5 px-2 py-0.5">
                      <span className="relative flex size-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent-deep-red)] opacity-75" />
                        <span className="relative inline-flex rounded-full size-2 bg-[var(--accent-deep-red)]" />
                      </span>
                      LIVE AGENT
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-4 sm:p-6 flex flex-col gap-5">
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="bg-muted/60 p-1 border border-border w-full grid grid-cols-3 gap-1">
                      <TabsTrigger value="voice" className="text-xs sm:text-sm font-medium gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all">
                        <Phone className="size-3.5" />
                        Voice Call
                      </TabsTrigger>
                      <TabsTrigger value="chat" className="text-xs sm:text-sm font-medium gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all">
                        <MessageSquare className="size-3.5" />
                        Web Chat
                      </TabsTrigger>
                      <TabsTrigger value="followup" className="text-xs sm:text-sm font-medium gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all">
                        <RefreshCw className="size-3.5" />
                        Follow-up
                      </TabsTrigger>
                    </TabsList>

                    <div className="mt-4 bg-muted/30 border border-border/80 rounded-lg p-4 min-h-[260px] max-h-[320px] overflow-y-auto flex flex-col gap-3 font-mono text-xs sm:text-sm">
                      <AnimatePresence mode="wait">
                        {activeTab === "voice" && <motion.div key="voice" initial={{
                        opacity: 0,
                        y: 8
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -8
                      }} transition={{
                        duration: 0.2
                      }} className="flex flex-col gap-3">
                            {voiceTranscript.map((msg, i) => <div data-index={i} key={i} className={`flex flex-col gap-1 p-2.5 rounded-md border ${msg.role === "ai" ? "bg-card border-border/90 text-card-foreground self-start max-w-[90%]" : "bg-primary/20 border-primary/40 text-foreground self-end max-w-[85%]"}`}>
                                <div className="flex items-center justify-between text-[11px] text-muted-foreground gap-2 font-sans">
                                  <span className="font-semibold text-foreground flex items-center gap-1">
                                    {msg.role === "ai" && <Bot className="size-3 text-[var(--accent-deep-red)]" />}
                                    {msg.sender}
                                  </span>
                                  <span>{msg.time}</span>
                                </div>
                                <p className="font-sans text-xs leading-normal">{msg.text}</p>
                              </div>)}
                          </motion.div>}

                        {activeTab === "chat" && <motion.div key="chat" initial={{
                        opacity: 0,
                        y: 8
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -8
                      }} transition={{
                        duration: 0.2
                      }} className="flex flex-col gap-3">
                            {chatTranscript.map((msg, i) => <div data-index={i} key={i} className={`flex flex-col gap-1 p-2.5 rounded-md border ${msg.role === "ai" ? "bg-card border-border/90 text-card-foreground self-start max-w-[90%]" : "bg-primary/20 border-primary/40 text-foreground self-end max-w-[85%]"}`}>
                                <div className="flex items-center justify-between text-[11px] text-muted-foreground gap-2 font-sans">
                                  <span className="font-semibold text-foreground flex items-center gap-1">
                                    {msg.role === "ai" && <Bot className="size-3 text-[var(--accent-deep-red)]" />}
                                    {msg.sender}
                                  </span>
                                  <span>{msg.time}</span>
                                </div>
                                <p className="font-sans text-xs leading-normal">{msg.text}</p>
                              </div>)}
                          </motion.div>}

                        {activeTab === "followup" && <motion.div key="followup" initial={{
                        opacity: 0,
                        y: 8
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -8
                      }} transition={{
                        duration: 0.2
                      }} className="flex flex-col gap-3">
                            {followupTranscript.map((msg, i) => <div data-index={i} key={i} className={`flex flex-col gap-1 p-2.5 rounded-md border ${msg.role === "ai" ? "bg-card border-border/90 text-card-foreground self-start max-w-[90%]" : "bg-primary/20 border-primary/40 text-foreground self-end max-w-[85%]"}`}>
                                <div className="flex items-center justify-between text-[11px] text-muted-foreground gap-2 font-sans">
                                  <span className="font-semibold text-foreground flex items-center gap-1">
                                    {msg.role === "ai" && <Bot className="size-3 text-[var(--accent-deep-red)]" />}
                                    {msg.sender}
                                  </span>
                                  <span>{msg.time}</span>
                                </div>
                                <p className="font-sans text-xs leading-normal">{msg.text}</p>
                              </div>)}
                          </motion.div>}
                      </AnimatePresence>

                      <div className="flex items-center gap-2 text-muted-foreground text-[11px] font-mono pt-1">
                        <Activity className="size-3.5 animate-pulse text-[var(--accent-deep-red)]" />
                        ClearLine AI status: Active & monitoring inbound traffic
                      </div>
                    </div>
                  </Tabs>

                  <div className="flex flex-col gap-2">
                    <span className="text-xs text-muted-foreground font-medium">
                      Quick Action Prompts:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {quickActions.map((action, i) => <Button data-index={i} key={action} type="button" variant="outline" size="sm" onClick={() => handleActionClick(action)} className="border-border text-foreground hover:bg-muted text-xs h-7 gap-1">
                          <ChevronRight className="size-3 text-[var(--accent-deep-red)]" />
                          {action}
                        </Button>)}
                    </div>
                    {quickInput && <p className="text-xs text-[var(--accent-deep-red)] font-mono pt-1">
                        Prompt selected: "{quickInput}" — click "Book a call" to deploy.
                      </p>}
                  </div>

                  <div className="flex flex-col gap-2 pt-1">
                    <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                      <Zap className="size-3.5 text-[var(--accent-deep-red)]" />
                      Live Network Events
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {liveFeedNotifications.map((item, i) => <div data-index={i} key={item.id} className="p-2.5 rounded-lg bg-muted/40 border border-border/70 flex flex-col gap-1">
                          <div className="flex items-center justify-between text-[11px]">
                            <Badge variant="secondary" className="text-[10px] bg-secondary text-secondary-foreground px-1.5 py-0">
                              {item.tag}
                            </Badge>
                            <span className="text-muted-foreground font-mono">
                              {item.time}
                            </span>
                          </div>
                          <span className="text-xs font-semibold text-foreground leading-tight">
                            {item.title}
                          </span>
                          <span className="text-[11px] text-muted-foreground truncate">
                            {item.subtitle}
                          </span>
                        </div>)}
                    </div>
                  </div>

                  <div className="pt-2 border-t border-border/80 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground font-mono">
                    <div className="flex items-center gap-1.5">
                      <Shield className="size-3.5 text-[var(--accent-deep-red)]" />
                      <span>Zero Hallucination Guard</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="size-3.5" />
                      <span>Avg Speed: 1.2s</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Check className="size-3.5 text-[var(--accent-deep-red)]" />
                      <span>Sync Status: 100% Clean</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}